import { createServerFn } from "@tanstack/solid-start";
import { z } from "zod";
import { withSentrySpan } from "~/lib/sentry-span";
import { authFunctionMiddleware } from "~/server/middleware/auth";
import {
  CreateFixedAssetSchema,
  UpdateFixedAssetSchema,
} from "./fixed-assets.schema";

export const FixedAssetIdInputSchema = z.object({
  id: z.string().trim().min(1),
  businessId: z.string().trim().min(1),
});

export const BulkDeleteFixedAssetsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  ids: z.array(z.string().trim().min(1)).min(1).max(200),
});

export const ListFixedAssetsInputSchema = z.object({
  businessId: z.string().trim().min(1),
  search: z.string().optional(),
  ids: z.array(z.string().trim().min(1)).optional(),
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
});

export const CreateFixedAssetInputSchema = z.object({
  businessId: z.string().trim().min(1),
  item: CreateFixedAssetSchema,
});

export const UpdateFixedAssetInputSchema = z.object({
  ...FixedAssetIdInputSchema.shape,
  updates: UpdateFixedAssetSchema,
});

export const listFixedAssets = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => ListFixedAssetsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("fixedAssets.list", async () => {
      const { listFixedAssetsForUser } = await import("./fixed-assets.server");
      return listFixedAssetsForUser(context.user.id, data.businessId, {
        search: data.search,
        ids: data.ids,
        offset: data.offset,
        limit: data.limit,
      });
    });
  });

export const getFixedAsset = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => FixedAssetIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("fixedAssets.get", async () => {
      const { getFixedAssetForUser } = await import("./fixed-assets.server");
      const result = await getFixedAssetForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const createFixedAsset = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => CreateFixedAssetInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("fixedAssets.create", async () => {
      const { createFixedAssetForUser } = await import("./fixed-assets.server");
      return createFixedAssetForUser(
        context.user.id,
        data.businessId,
        data.item,
      );
    });
  });

export const updateFixedAsset = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => UpdateFixedAssetInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("fixedAssets.update", async () => {
      const { updateFixedAssetForUser } = await import("./fixed-assets.server");
      const result = await updateFixedAssetForUser(
        context.user.id,
        data.id,
        data.updates,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const deleteFixedAsset = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => FixedAssetIdInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("fixedAssets.delete", async () => {
      const { deleteFixedAssetForUser } = await import("./fixed-assets.server");
      const result = await deleteFixedAssetForUser(
        context.user.id,
        data.businessId,
        data.id,
      );
      if (!result) throw Response.json({ error: "Not found" }, { status: 404 });
      return result;
    });
  });

export const bulkDeleteFixedAssets = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .validator((i: unknown) => BulkDeleteFixedAssetsInputSchema.parse(i))
  .handler(async ({ context, data }) => {
    return withSentrySpan("fixedAssets.bulkDelete", async () => {
      const { bulkDeleteFixedAssetsForUser } =
        await import("./fixed-assets.server");
      return bulkDeleteFixedAssetsForUser(
        context.user.id,
        data.businessId,
        data.ids,
      );
    });
  });
